import { IApiClient } from '@base/features/api/model/apiClient';
import {
  AccountStatement,
  PersonalInfo,
  PlayerStatistics,
  ReferalStatistics,
  IUserService,
} from './interfaces';
import LocalStorage from '../../storage/localStorage';
import { StorageKeys } from '../../../utils/constants';
import { ResponseUserAuthData } from '@base/features/auth/model/interfaces';
import { deserialize } from 'object-deserializer';
import {
  accountStatementDeserializer,
  partialAuthDataFaceDeserializer,
  referalStatisticsDeserializer,
} from './deserializers';
import { responseUserAuthDataDeserializer } from './deserializers';
import { RangeValue } from '@ui-kit/components/rangePicker';
import addRangeToParams from '@base/utils/addRangeToParams';
import API_PATHS from '@base/features/api/constants/apiPaths';
import { t } from '@base/utils/helpers';
import { IModalProvider } from '@base/features/modal/model/interfaces';

export default class UserService implements IUserService {
  constructor(
    private apiClient: IApiClient,
    private localStorage: LocalStorage,
    private modalProvider: IModalProvider,
    private gameId: number
  ) {}

  async changeGender(gender: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'gender',
        json: 1,
        gender,
      },
      API_PATHS.corner
    );

    const data = await res.json();

    if (data.ok !== 1) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  isGuestPlayer(): boolean {
    const userAuthData = this.localStorage.get(StorageKeys.SESSION);
    return userAuthData.guestUser;
  }

  openGuestModal(onClick: () => void): void {
    const title = t('ooops!');
    const body = t('alert_for_guest');
    const buttons = [
      {
        title: t('registration'),
        onClick: () => {
          onClick();
          this.modalProvider.hide();
        },
      },
    ];
    this.modalProvider.show({
      title,
      body,
      buttons,
    });
  }

  async changeMaritalStatus(status: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'mstatus',
        json: 1,
        mstatus: status,
      },
      API_PATHS.corner
    );

    const data = await res.json();

    if (data.ok !== 1) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async changeAbout(about: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'about',
        json: 1,
        about,
      },
      API_PATHS.corner
    );
    const data = await res.json();
    return data;
  }

  async changeYearOfBirth(yob: moment.Moment): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'yob',
        json: 1,
        yob: yob.year().toString(),
      },
      API_PATHS.corner
    );

    const data = await res.json();

    if (data.ok !== 1) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async changeAddress(address: {
    city: string;
    street: string;
    zip: string;
    state?: string;
    country: string;
  }): Promise<void> {
    const options = {
      do: 'change_address',
      json: 1,
      region: address.country === 'US' ? 'us' : 'int',
      city: address.city,
      street: address.street,
      zip: address.zip,
    };

    let res;
    try {
      if (address.country === 'US') {
        if (!address.state) throw new Error('State is required for us citizens.');
        res = await this.apiClient.authPost(
          {
            ...options,
            state: address.state,
          },
          API_PATHS.api
        );
      } else {
        if (!address.country) throw new Error('Country is required for non us citizens.');
        res = await this.apiClient.authPost(
          {
            ...options,
            country: address.country.toLowerCase(),
          },
          API_PATHS.api
        );
      }
    } catch (e: any) {
      throw new Error(e);
    }

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async changePhone(newPhone: string, firebaseToken: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'setphone',
        json: 1,
        phone: newPhone,
        token: firebaseToken,
      },
      API_PATHS.api
    );

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async changeEmail(newEmail: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'change_email',
        json: 1,
        new_email: newEmail,
      },
      API_PATHS.api
    );

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
    this.modalProvider.show({
      body: 'Confirmation email was sent to your email address. Plase check your inbox.',
      headerBtn: true,
      closeModal: () => this.modalProvider.hide,
    });
  }

  async changeName(name: { firstName: string; lastName: string }): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'change_name',
        json: 1,
        firstname: name.firstName,
        lastname: name.lastName,
      },
      API_PATHS.api
    );

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async changeLogin(newLogin: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'change_login',
        json: 1,
        new_login: newLogin,
      },
      API_PATHS.api
    );

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async changePassword(oldPassword: string, newPassword: string, newPasswordConfirm: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'change_password',
        json: 1,
        newPassword: newPassword,
        newPassword2: newPasswordConfirm,
        oldPassword: oldPassword,
      },
      API_PATHS.api
    );

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async unsubscribeAnnoucements(): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'unsubscribe',
        json: 1,
      },
      API_PATHS.api
    );

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async subscribeAnnoucements(): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'subscribe',
        json: 1,
      },
      API_PATHS.api
    );

    const data = await res.json();

    if (data.code !== 0) {
      throw new Error(data.descr || 'Unknown error');
    }
  }

  async getReferalFeeStatistics(range?: RangeValue): Promise<ReferalStatistics> {
    const params = addRangeToParams({ json: 1 }, range);
    const res = await this.apiClient.authGet(params, API_PATHS.stats);
    const data = await res.json();
    return deserialize(data, referalStatisticsDeserializer);
  }

  async getAvatar(playerId: string): Promise<any> {
    const params = { uid: playerId };
    const response = await this.apiClient.post(params, API_PATHS.avatar);
    if (!response) throw new Error('missing response');
    return response.text();
  }

  async getPlayerStats(playerId: string): Promise<PlayerStatistics> {
    const params = {
      gid: this.gameId,
      uid: playerId,
      json: 1,
    };
    const res = await this.apiClient.post(params, API_PATHS.playerInfo);
    const data = await res.json();
    return data;
    // return deserialize(data, playerStatisticsDeserializer);
  }


  async getUserInfo(): Promise<PersonalInfo> {
    const res = await this.apiClient.authPost(
      {
        do: 'account',
        json: 1,
      },
      API_PATHS.api
    );

    const data = await res.json();
    return data;
    // return deserialize(data, personalInfoDeserializer);
  }

  async getPartAccountInfo(part: string, usr?: string, session?: string): Promise<any> {
    const res = await this.apiClient.authPost(
      {
        do: 'account',
        part: part,
      },
      API_PATHS.api
    );
    const data = await res.json();
    if (part === 'face') {
      return deserialize(data, partialAuthDataFaceDeserializer);
    }
    if (part === 'tickets') {
      return data.acct_value;
    }
    return deserialize(data, responseUserAuthDataDeserializer);
  }

  async getAccountStatement(range?: RangeValue): Promise<AccountStatement> {
    const params = {
      do: 'account_statement',
    };
    const res = await this.apiClient.authPost(addRangeToParams(params, range), API_PATHS.api);
    const data = await res.json();
    return deserialize(data, accountStatementDeserializer);
  }

  async setSystemAvatar(id: number): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.authPost(
      {
        do: 'change_face',
        id: id,
      },
      API_PATHS.api
    );
    return await res.json();
  }

  async setSocialAvatar(url: string): Promise<any> {
    const res = await this.apiClient.authPost(
      {
        do: 'change_face',
        avaurl: url,
      },
      API_PATHS.api
    );
    return await res.json();
  }

  //avatar as file by user
  async setCustomAvatar(url: string, formData: FormData): Promise<ResponseUserAuthData> {
    try {
      const res = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      const data = res.json();
      return deserialize(data, responseUserAuthDataDeserializer);
    } catch (e: any) {
      throw new Error(e);
    }
  }

  async setLogin(usr: string, session: string, newLogin: string): Promise<any> {
    const res = await this.apiClient.post(
      {
        do: 'change_login',
        new_login: newLogin,
        usr: usr,
        sid: session,
      },
      API_PATHS.api
    );
    return await res.json();
  }

  async canBuyTickets(usr: string, session: string): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'can_buy',
        sid: session,
        usr: usr,
      },
      API_PATHS.api
    );
    const data = res.json();
    return deserialize(data, responseUserAuthDataDeserializer);
  }

}
